import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Text, TextProps } from '../Text';
import { Link } from '../Link';
import { Box } from '../Box';
import { Heading, HeadingProps } from '../Heading';
import { List } from '../List';
import { Divider } from '../Divider';
import {
  rehypeAlert,
  rehypeTruncate,
  TruncationSettings,
  validateAccessibleHeadingStyles,
} from './Markdown.utils';
import { assert } from '~/utils/assert';
import { Flex } from '../Flex';
import { SubtleNotice } from './SubtleNotice';
import { TypographyToken } from '~/design';
import { flushSync } from 'react-dom';

export type HeadingStyle = {
  variant: Extract<
    TypographyToken,
    | 'bodyM'
    | 'titleS'
    | 'titleM'
    | 'titleL'
    | 'headlineS'
    | 'headlineM'
    | 'headlineL'
    | 'displayS'
    | 'displayM'
    | 'displayL'
  >;
  contrast?: 'mid';
  bold?: true;
};

export type HeadingLevelStyles = {
  h1?: HeadingStyle;
  h2?: HeadingStyle;
  h3?: HeadingStyle;
  h4?: HeadingStyle;
  h5?: HeadingStyle;
  h6?: HeadingStyle;
};

export type MarkdownProps = {
  /**
   * Passed to the Links' `mode` prop.  See `Link.mode` for more info
   */
  linkMode?: 'override_user_choice_and_open_in_new_tab';

  /** The markdown content to render. */
  markdown: string;

  onErrorDontUseThisAndIfYouDoYouSuck?: (err: Error) => void;

  /**
   * The settings to use for paragraph and other text elements
   */
  textSettings?: {
    variant: 'bodyS' | 'bodyM';
    contrast?: 'mid';
  };

  headingSettings: {
    /**
     * Specifies what semantic heading level to start at when rendering headings.
     *
     * This is useful when there are other headings on the page before the
     * markdown.  For instance, if there's an `h1` and `h2` already on the page,
     * then you would likely want to set `baseLevel` to `3` so the first heading
     * rendered in the markdown is `<h3>`
     *
     * This prop is required, because it's almost always necessary to set this
     * in order to avoid accessibility issues
     *
     * @example
     * If `baseLevel` is set to `3`, then the following markdown:
     * ```md
     * # My heading
     * Some content
     *
     * ## My other heading
     * Some more content
     * ```
     * Would be rendered as the following HTML
     * ```html
     * <h3>My heading</h3>
     * <p>Some content</p>
     * <h4>My other heading</h4>
     * <p>Some more content</p>
     * ```
     */
    baseLevel: 1 | 2 | 3 | 4 | 5 | 6;

    /**
     * The maximum relative heading level that is allowed (before considering `baseLevel`).
     *
     * This should be less than or equal to `6 - baseLevel + 1`.  For instance,
     * if `baseLevel` is `3`, then `maxLevel` should be `4` or less.  This is
     * because there are only 6 headings levels in HTML, so if `baseLevel` is
     * `3`, then there are only 4 heading levels available (h3, h4, h5, h6)
     *
     * Consider settings this to a lower value to give yourself room to add more
     * headings later if needed
     *
     * Set to `0` to disable all headings
     */
    maxLevel?: 0 | 1 | 2 | 3 | 4 | 5 | 6;

    /**
     * The styles to apply to each relative heading level
     */
    styles?: HeadingLevelStyles;
  };

  /**
   * The options for truncating the markdown content.
   */
  truncationSettings?: TruncationSettings;
};

type ListItemChild = { type: 'li'; props: { children: Array<string> } };

// `react-markdown` unfortunately does not export its types.
// Using this custom type as a workaround since we need to use typeguards to ensure type safety
type ReactMarkdownComponentProp = {
  children: Array<ReactNode | string | ListItemChild>;
  href?: string;
};

export function Markdown({
  linkMode,
  markdown,
  onErrorDontUseThisAndIfYouDoYouSuck,
  headingSettings,
  textSettings,
  truncationSettings,
}: MarkdownProps) {
  const [truncated, setTruncated] = useState(true);
  assert(
    headingSettings?.baseLevel,
    'A base heading level must be provided.  Please see https://applyboard.atlassian.net/wiki/spaces/UE/pages/2681012586/Heading+Accessibility#Be-careful-when-rendering-user-generated-content',
  );

  validateAccessibleHeadingStyles(headingSettings);

  const handleError = useCallback(
    (message: string) => {
      const err = new Error(message);
      if (onErrorDontUseThisAndIfYouDoYouSuck) {
        onErrorDontUseThisAndIfYouDoYouSuck(err);
      } else {
        throw err;
      }
    },
    [onErrorDontUseThisAndIfYouDoYouSuck],
  );

  useMemo(() => {
    // Disallow skipping heading levels for a11y reasons. See this doc for more info:
    // https://applyboard.atlassian.net/wiki/spaces/UE/pages/2681012586/Heading+Accessibility

    const lines = (markdown.split('\n') ?? [])
      .map(line => line.trim())
      .filter(line => line.startsWith('#'));
    if (lines.length === 0) {
      return;
    }

    let currentDepth = getDepth(lines[0]);
    if (currentDepth !== 1) {
      handleError(
        `[Markdown] Invalid markdown: headings should start with an h1. (Found: h${currentDepth})`,
      );
    }

    for (const line of lines) {
      const depth = getDepth(line);
      if (depth - currentDepth >= 2) {
        handleError(
          `[Markdown] Invalid markdown: headings are not well-structured. (Moved from h${currentDepth} to h${depth}.)`,
        );
      }
      currentDepth = depth;
    }
  }, [markdown, handleError]);

  const containerRef = useRef<HTMLDivElement>(null);
  return (
    <div ref={containerRef}>
      <Flex direction="column" gap={4}>
        <ReactMarkdown
          components={{
            button(props) {
              return (
                <Text as="span">
                  <Link
                    variant="inline"
                    as="button"
                    onClick={() => {
                      flushSync(() => {
                        setTruncated(!truncated);
                      });
                      containerRef.current?.querySelectorAll('button')[0]?.focus();
                    }}
                  >
                    {props.children}
                  </Link>
                </Text>
              );
            },
            a(props: ReactMarkdownComponentProp) {
              if (!props.href) {
                handleError('A URL must be provided for links.');
                return <TextWrapper as="span" componentProps={props} containerAs="span" />;
              }
              return (
                <Link href={props.href} mode={linkMode}>
                  {(props.children ?? []).map(child => child)}
                </Link>
              );
            },
            blockquote(props) {
              if (props.className) {
                assert(
                  props.className === 'note' ||
                    props.className === 'tip' ||
                    props.className === 'warning' ||
                    props.className === 'caution',
                );

                return <SubtleNotice importance={props.className}>{props.children}</SubtleNotice>;
              }

              return (
                <Box pb={2}>
                  <Box emphasis="highlighted" p={4}>
                    <blockquote css={{ margin: 0 }}>{props.children}</blockquote>
                  </Box>
                </Box>
              );
            },
            code: 'blockquote',
            h1({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 1) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h1}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            h2({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 2) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel + 1}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h2}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            h3({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 3) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel + 2}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h3}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            h4({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 4) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel + 3}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h4}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            h5({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 5) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel + 4}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h5}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            h6({ children }) {
              if (typeof headingSettings.maxLevel === 'number' && headingSettings.maxLevel < 6) {
                handleError(`Exceeded maximum heading level of ${headingSettings.maxLevel}`);
              }
              return (
                <HeadingWrapper
                  level={headingSettings.baseLevel + 5}
                  handleError={handleError}
                  styleSettings={headingSettings.styles?.h6}
                >
                  {children}
                </HeadingWrapper>
              );
            },
            hr() {
              return <Divider />;
            },
            img() {
              handleError('Images are not currently supported by this component.');
              return <span>(image)</span>;
            },
            p(props: ReactMarkdownComponentProp) {
              return (
                <TextWrapper
                  componentProps={props}
                  variant={textSettings?.variant}
                  contrast={textSettings?.contrast}
                />
              );
            },
            ol(props: ReactMarkdownComponentProp) {
              return <ListWrapper componentProps={props} isOrdered />;
            },
            ul(props: ReactMarkdownComponentProp) {
              return <ListWrapper componentProps={props} />;
            },
          }}
          rehypePlugins={[
            rehypeAlert,
            [
              // @ts-expect-error FIXME: Not sure how to fix this
              rehypeTruncate,
              {
                truncated,
                truncationSettings,
              },
            ],
          ]}
        >
          {markdown}
        </ReactMarkdown>
      </Flex>
    </div>
  );
}

function HeadingWrapper({
  children,
  handleError,
  level: _level,
  styleSettings,
}: {
  children: React.ReactNode;
  handleError?: (message: string) => void;
  level: number;
  styleSettings: HeadingStyle | undefined;
}) {
  let level = _level;
  if (_level > 6) {
    if (handleError) {
      handleError('Headings cannot exceed a level of 6.');
    } else {
      throw new Error('Headings cannot exceed a level of 6.');
    }
    level = 6 as const;
  }

  return (
    <div
      css={({ spacing }) => ({
        // Headings should be close to the content that follows them, so it's more
        // clear the heading is "labelling" the content below it
        marginBottom: `-${spacing[3]}`,
      })}
    >
      <Heading
        level={level as HeadingProps['level']}
        variant={styleSettings?.variant || getHeadingVariant(level)}
        contrast={styleSettings?.contrast}
      >
        <span style={{ fontWeight: styleSettings?.bold ? 700 : undefined }}>{children}</span>
      </Heading>
    </div>
  );
}

function ListWrapper({
  componentProps,
  isOrdered,
}: {
  componentProps: ReactMarkdownComponentProp;
  isOrdered?: boolean;
}) {
  const listItems = (componentProps.children ?? [])
    .map(item => {
      if (isListitemChild(item)) {
        return item?.props.children;
      }
    })
    .filter(Boolean);

  return (
    <List ordered={isOrdered}>
      {listItems.map((item, index) => (
        <List.Item key={index}>{(item ?? [])?.map(item => item)}</List.Item>
      ))}
    </List>
  );
}

function TextWrapper({
  as = 'p',
  componentProps,
  containerAs: Tag = 'div',
  variant = 'bodyM',
  contrast,
}: {
  as?: TextProps['as'];
  componentProps: ReactMarkdownComponentProp;
  containerAs?: 'div' | 'span';
  variant?: TextProps['variant'];
  contrast?: TextProps['contrast'];
}) {
  return (
    <Tag
      css={{
        whiteSpace: 'pre-line',
      }}
    >
      <Text wordBreak="word" variant={variant} contrast={contrast} as={as}>
        {(componentProps.children ?? []).map(child => child)}
      </Text>
    </Tag>
  );
}

function isListitemChild(value: unknown): value is ListItemChild {
  return typeof value === 'object' && value !== null && 'type' in value && value.type === 'li';
}

const getDepth = (line: string) => line.split('').findIndex(char => char !== '#');

export function getHeadingVariant(level: number | undefined) {
  return level === 1
    ? 'headlineL'
    : level === 2
      ? 'headlineM'
      : level === 3
        ? 'headlineS'
        : level === 4
          ? 'titleL'
          : level === 5
            ? 'titleM'
            : level === 6
              ? 'titleS'
              : 'bodyM';
}

export { validateAccessibleHeadingStyles };
